import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { X, Lock, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface Props {
  publishableKey: string;
  clientSecret: string;
  amount: number;
  currency: string;
  invoiceName?: string;
  packageName?: string;
  onSuccess: (paymentIntentId: string) => Promise<void> | void;
  onClose: () => void;
}

function CheckoutForm({
  amount,
  currency,
  invoiceName,
  packageName,
  onSuccess,
  onClose,
}: {
  amount: number;
  currency: string;
  invoiceName?: string;
  packageName?: string;
  onSuccess: (id: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || loading || verifying) return;

    setLoading(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment confirmation failed');
        setLoading(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        setLoading(false);
        setVerifying(true);
        toast.success('Payment succeeded! Activating subscription...');
        try {
          await onSuccess(paymentIntent.id);
        } catch (verErr: any) {
          setErrorMessage(verErr?.message || 'Payment succeeded, but subscription activation encountered an error.');
        } finally {
          setVerifying(false);
        }
      } else {
        setLoading(false);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'An unexpected error occurred.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gradient-to-r from-indigo-50/80 via-blue-50/60 to-slate-50 border border-indigo-100/80 rounded-xl p-3.5 flex items-center justify-between shadow-sm">
        <div>
          <span className="text-[10px] font-black uppercase tracking-wider text-indigo-700/80">
            Payment Target
          </span>
          <p className="text-sm font-bold text-gray-900 mt-0.5">
            {invoiceName ? `Invoice ${invoiceName}` : (packageName || 'SaaS Subscription')}
          </p>
        </div>
        <div className="text-right">
          <span className="text-[10px] font-black uppercase tracking-wider text-indigo-700/80">
            Amount Due
          </span>
          <p className="text-lg font-black text-indigo-950 tabular-nums">
            {currency} {amount.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="border border-gray-200/80 rounded-xl p-3.5 bg-gray-50/40">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5 text-xs text-red-700 animate-in fade-in">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
          <div className="flex-1 font-medium">{errorMessage}</div>
        </div>
      )}

      {verifying && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2.5 text-xs text-emerald-800 animate-in fade-in">
          <Loader2 className="w-4 h-4 shrink-0 animate-spin text-emerald-600" />
          <span className="font-semibold">Payment confirmed! Synchronizing subscription status...</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={loading || verifying}
          className="px-4 py-2.5 rounded-xl border border-gray-300 text-xs font-bold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={!stripe || loading || verifying}
          className="flex-1 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading || verifying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{verifying ? 'Activating...' : 'Processing Securely...'}</span>
            </>
          ) : (
            <>
              <Lock className="w-3.5 h-3.5" />
              <span>Pay {currency} {amount.toFixed(2)}</span>
            </>
          )}
        </button>
      </div>

      <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1.5 pt-1">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
        <span>256-bit SSL encrypted. Card details are processed securely by Stripe.</span>
      </p>
    </form>
  );
}

export default function StripePaymentModal({
  publishableKey,
  clientSecret,
  amount,
  currency,
  invoiceName,
  packageName,
  onSuccess,
  onClose,
}: Props) {
  const stripePromise = React.useMemo(() => {
    return loadStripe(publishableKey);
  }, [publishableKey]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto">
      <div className="relative w-full max-w-lg my-auto rounded-2xl bg-white shadow-2xl border border-gray-200/90 flex flex-col max-h-[92vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Secure Online Checkout</h3>
              <p className="text-[11px] text-gray-500">Pay via Credit / Debit Card, Apple Pay, or Google Pay</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-5 overflow-y-auto flex-1">
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#4f46e5',
                  borderRadius: '10px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                },
              },
            }}
          >
            <CheckoutForm
              amount={amount}
              currency={currency}
              invoiceName={invoiceName}
              packageName={packageName}
              onSuccess={onSuccess}
              onClose={onClose}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
}
